import { api } from '@/convex/_generated/api'
import { convex, whenConvexReady } from '@/lib/convex-client'

/**
 * The shape of the user's day, as the planner sees it.
 *
 * These five values were readable by `convex/scheduling.ts` long before
 * anything could write them — they were absent from `setPreferences` and from
 * every screen, so every user silently got the hardcoded 9:00–18:00 fallback.
 * This is the write path that was missing.
 */

/** Matches the planner's fallbacks in `convex/scheduling.ts`. */
export const DAY_SHAPE_DEFAULTS = {
  workdayStartMinutes: 9 * 60,
  workdayEndMinutes: 18 * 60,
  planBufferMinutes: 5,
} as const

export interface DayShape {
  workdayStartMinutes: number
  workdayEndMinutes: number
  planBufferMinutes: number
  /** Both absent means "no preference" — the planner then treats the whole working window as peak. */
  peakStartMinutes?: number
  peakEndMinutes?: number
}

/**
 * How the day is asked about before the user has a vocabulary for it.
 *
 * Onboarding cannot ask for a focus window in clock time — nobody knows their
 * peak to the quarter hour on day one — so it asks for a *part of the day* and
 * this derives the window. Kept here with the shape it produces, and pure, so
 * the mapping can be reasoned about without mounting the step.
 */
export type FocusChoice = 'early' | 'midday' | 'late' | 'none'

export interface HoursDraft {
  workdayStartMinutes: number
  workdayEndMinutes: number
  focus: FocusChoice
}

export const HOURS_DRAFT_DEFAULTS: HoursDraft = {
  workdayStartMinutes: DAY_SHAPE_DEFAULTS.workdayStartMinutes,
  workdayEndMinutes: DAY_SHAPE_DEFAULTS.workdayEndMinutes,
  focus: 'none',
}

/** Three hours is about as long as deep work honestly lasts. */
const FOCUS_BLOCK_MINUTES = 180

/**
 * The chosen part of the day as the window the planner reads.
 *
 * Always clamped inside the working window: a focus window outside the hours
 * the user just gave would silently place nothing.
 */
export function peakWindowFor(
  draft: HoursDraft,
): { peakStartMinutes: number; peakEndMinutes: number } | null {
  const { workdayStartMinutes: start, workdayEndMinutes: end, focus } = draft
  if (focus === 'none' || end <= start) return null

  const span = end - start
  const block = Math.min(FOCUS_BLOCK_MINUTES, span)

  if (focus === 'early') return { peakStartMinutes: start, peakEndMinutes: start + block }
  if (focus === 'late') return { peakStartMinutes: end - block, peakEndMinutes: end }

  // Clamped to the window rather than trusted to fit: rounding a very short day
  // can push the midday block a minute past its end, which `setPreferences`
  // rejects outright.
  const middle = start + Math.round(span / 2)
  const half = Math.round(block / 2)
  const peakStartMinutes = Math.max(start, middle - half)
  const peakEndMinutes = Math.min(end, middle + half)
  return peakEndMinutes > peakStartMinutes ? { peakStartMinutes, peakEndMinutes } : null
}

/** The draft as the day shape the rest of the app stores. */
export function draftToDayShape(draft: HoursDraft): DayShape {
  const peak = peakWindowFor(draft)
  return {
    workdayStartMinutes: draft.workdayStartMinutes,
    workdayEndMinutes: draft.workdayEndMinutes,
    planBufferMinutes: DAY_SHAPE_DEFAULTS.planBufferMinutes,
    peakStartMinutes: peak?.peakStartMinutes,
    peakEndMinutes: peak?.peakEndMinutes,
  }
}

export async function loadDayShape(): Promise<DayShape> {
  await whenConvexReady()
  const user = await convex.query(api.users.current, {})

  return {
    workdayStartMinutes: user?.workdayStartMinutes ?? DAY_SHAPE_DEFAULTS.workdayStartMinutes,
    workdayEndMinutes: user?.workdayEndMinutes ?? DAY_SHAPE_DEFAULTS.workdayEndMinutes,
    planBufferMinutes: user?.planBufferMinutes ?? DAY_SHAPE_DEFAULTS.planBufferMinutes,
    peakStartMinutes: user?.peakStartMinutes,
    peakEndMinutes: user?.peakEndMinutes,
  }
}

export async function saveDayShape(shape: DayShape): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.users.setPreferences, {
    workdayStartMinutes: shape.workdayStartMinutes,
    workdayEndMinutes: shape.workdayEndMinutes,
    planBufferMinutes: shape.planBufferMinutes,
    // Explicit null clears the focus window; undefined would leave the old one
    // in place and make the toggle look broken.
    peakStartMinutes: shape.peakStartMinutes ?? null,
    peakEndMinutes: shape.peakEndMinutes ?? null,
  })
}
